import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import ogHeroImage from "../../assets/football-grass.webp";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { activityFeeText } from "@/lib/activity-fees";
import football from "../../assets/football-grass.webp";

const title = "Malmö FF Football Academy in Spain | SIS Estepona";
const description =
  "Combine professional football training using Malmö FF's methodology with a Swedish education in Estepona/Marbella.";

export const Route = createFileRoute("/en/football-academy")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/football-academy") },
      { name: "twitter:card", content: "summary_large_image" },
      ...ogImage("/og/fotboll-v1.jpg"),
      { property: "og:locale", content: "en_GB" },
    ],
    links: seoLinks("/en/football-academy"),
  }),
  component: FootballAcademy,
});

function FootballAcademy() {
  const fee = activityFeeText("football", "en");
  return (
    <>
      <PageHero
        eyebrow="Sports profile"
        title="Malmö FF's football academy with us"
        intro="Swedish International School and Malmö FF's football academy offer talented young footballers a unique chance to combine professional training with a first-class education on the Costa del Sol."
      />

      <Prose>
        <img
          src={football}
          alt="Football training on a full-size grass pitch at the municipal stadium in Estepona"
          width={1200}
          height={900}
          loading="lazy"
          className="rounded-xl shadow-soft"
        />

        <h2>Study in Spain and train with Malmö FF</h2>
        <p>
          Pupils train according to Malmö FF's proven methodology while studying in an international
          and inspiring school environment. Training is led by experienced instructors and takes
          place in the mornings at two municipal stadiums: Estadio Municipal Francisco Muñoz Pérez in
          Estepona (about 13 km, roughly 14 minutes by car) and Estadio Municipal Antonio Naranjo in
          San Pedro Alcántara (about 11 km, roughly 15 minutes). Lessons are arranged to ensure an
          optimal balance between sport and studies.
        </p>
        <p>
          With individual development plans and a structured daily routine, we create the best
          possible conditions for pupils to succeed – both on and off the pitch. Alongside technique,
          tactics and physical training, the academy also works on team spirit, discipline and
          leadership.
        </p>

        <h2>Who can apply?</h2>
        <p>
          The football academy is open to pupils in year 7 or higher who have a school place at
          Swedish International School. To make training and schoolwork sustainable, lessons are
          delivered through Sofia Distans with guidance and support from our teachers on site in
          Estepona. You do not need to play at elite level today, but you should have a genuine
          interest in football and a desire to develop as both a player and a person.
        </p>

        <h2>A day at the academy</h2>
        <p>
          Training is part of the school day, with three sessions a week (Tuesday, Wednesday and Thursday in the 2026/27 school year), 09.00–10.00.
          During the sessions, players receive varied and developmental training covering every aspect
          of the game: technique, passing, dribbling, coordination, game understanding and match
          situations. The training is adapted for both outfield players and goalkeepers, with a
          focus on individual development.
        </p>
        <p>
          The day starts with warm-up and coordination work, then moves on to technique, game
          understanding and match situations for both outfield players and goalkeepers. After training
          there is time for lunch and rest before studies begin. Lessons are delivered through Sofia
          Distans, following the Swedish curriculum with support from our teaching team. Our
          teachers are on hand to explain, support and follow up each pupil's progress.
        </p>
        <p>
          Between training, studies and meals, pupils have time to relax, spend time with classmates
          and experience life on the Costa del Sol. We help them build habits that work both on and
          beside the pitch: planning, recovery and taking responsibility.
        </p>

        <h2>How it works</h2>
        <ul>
          <li>Training according to Malmö FF's methodology with experienced coaches.</li>
          <li>Individual development plans for every player.</li>
          <li>Training in the mornings, with studies arranged around training.</li>
          <li>
            The academy requires schooling through Sofia Distans and a school place at Swedish
            International School.
          </li>
          <li>
            {fee
              ? `Fee: ${fee}, in addition to the school fee. Booked one term at a time.`
              : "Fee: see the fees page for the current academy fee, in addition to the school fee."}
          </li>
        </ul>
        <p>If your child takes part in Malmö FF's football academy, the school helps with the entire application process and the terms that apply to participation.</p>

        <h2>More than football</h2>
        <p>
          The academy is not only about becoming a better footballer. It also trains qualities that
          help in school and later working life: collaborating under pressure, giving and receiving
          feedback, handling both success and setbacks, and taking responsibility for your own day
          and development. We see every player as a whole person, and our goal is to give them a
          strong foundation that lasts long after they leave school.
        </p>

        <p>
          Read more about Malmö FF's school academies at{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.mff.se/mff-i-samhallet/skolakademier/"
            target="_blank"
            rel="noreferrer"
          >
            mff.se
          </a>
          .
        </p>
      </Prose>

      <CtaBand
        title="Apply for the football academy"
        text="Apply to receive information about trials and the admissions process."
      />
      <div className="h-20" />
    </>
  );
}
